"use client"

import { useFormatter } from "@/hooks/use-formatter"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { toast } from "sonner"
import { useTranslations } from "next-intl"
import { differenceInCalendarDays, format as formatDateFns } from "date-fns"
import {
  IconCalendar,
  IconCheck,
  IconLoader2,
  IconMapPin,
  IconPlaneArrival,
  IconPlaneDeparture,
  IconUpload,
  IconX,
} from "@tabler/icons-react"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { TIME_24H_PATTERN, TimeInput, TimeOrTextInput } from "@/components/ui/time-input"
import { Textarea } from "@/components/ui/textarea"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import {
  createLeadTravel,
  getIdentityDocumentStatus,
  getTravelChecks,
  updateLeadTravel,
  type TravelChecks,
} from "@/lib/travel-api"
import { HotelCombobox } from "@/components/leads/hotel-combobox"
import { CityCombobox } from "@/components/leads/city-combobox"
import { VisitPurposeSelect } from "@/components/leads/visit-purpose-select"
import { useCurrency, useFormatCurrency, getCurrencySymbol } from "@/hooks/use-currency"
import { getHotels } from "@/lib/hotels-api"
import { findHotelStayConflict } from "@/lib/hotel-stay-conflict"
import type { Hotel } from "@/types"
import {
  deleteAttachment,
  getAttachments,
  uploadAttachment,
  type AttachmentDocument,
} from "@/lib/attachments-api"
import { getLeadVisits, getVisitPurposes } from "@/lib/clinical-visits-api"
import { fareOtherShare, parseFareInput } from "@/lib/fare-split"
import { FareSplit, type FareParty } from "@/components/travel/fare-split"
import { useAuthStore } from "@/stores/auth-store"
import { hasRole, ROLES } from "@/lib/roles"
import { cn } from "@/lib/utils"
import type {
  ClinicalVisit,
  CreateLeadTravelData,
  LeadTravel,
  TravelPayer,
  VisitPurposesMap,
} from "@/types"

const NO_PURPOSE = "__none__"

const ACCEPTED_FILES = ".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic"

const createTravelSchema = (
  t: (key: string) => string,
  purposeRequired: boolean,
) => z.object({
  arrival_date: z.string().optional().or(z.literal("")),
  // Optional beside the date. Blank stays blank all the way to the column —
  // the calendar pins a time-less trip to the whole day rather than inventing
  // a clock time for it (TravelEventProvider).
  // 24-hour "HH:MM" — the TimeInput masks keystrokes but a half-typed draft
  // ("14:" / "9") must not reach the API's date_format:H:i rule as a 422.
  arrival_time: z
    .string()
    .optional()
    .or(z.literal(""))
    .refine((v) => !v || TIME_24H_PATTERN.test(v), {
      message: t("travelForm.invalidTime"),
    }),
  // Radiance 261019 — the other clock of each flight: the flight in's take-off
  // from home, the flight home's landing there. Flag-gated
  // (travel_flight_both_times_enabled), same 24-hour rule as the times above.
  arrival_takeoff_time: z
    .string()
    .optional()
    .or(z.literal(""))
    .refine((v) => !v || TIME_24H_PATTERN.test(v), {
      message: t("travelForm.invalidTime"),
    }),
  arrival_airport: z.string().max(100).optional().or(z.literal("")),
  // Radiance 261001 — where the patient flies FROM ("Flying from"), typed off
  // the ticket; the logistics desk's travel sheet prints it as their city.
  // Flag-gated (travel_origin_city_enabled), free text like the airports.
  origin_city: z.string().max(100).optional().or(z.literal("")),
  departure_date: z.string().optional().or(z.literal("")),
  departure_time: z
    .string()
    .optional()
    .or(z.literal(""))
    .refine((v) => !v || TIME_24H_PATTERN.test(v), {
      message: t("travelForm.invalidTime"),
    }),
  departure_landing_time: z
    .string()
    .optional()
    .or(z.literal(""))
    .refine((v) => !v || TIME_24H_PATTERN.test(v), {
      message: t("travelForm.invalidTime"),
    }),
  departure_airport: z.string().max(100).optional().or(z.literal("")),
  // Who pays for the flight / the hotel. False = the clinic covers it, which
  // is what every trip recorded before the flags existed reads as.
  client_pays_flight: z.boolean(),
  // ArtDent 260922 — the three-way answer the booleans are derived from
  // server-side: the clinic, the patient, or the referral partner.
  flight_payer: z.enum(["clinic", "patient", "affiliator"]),
  hotel_payer: z.enum(["clinic", "patient", "affiliator"]),
  // How many of the stay's nights the other party carries. String so the box
  // can be empty mid-typing; clamped to the stay on submit.
  hotel_patient_nights: z.string().optional().or(z.literal("")),
  // Fare the patient paid. String so the box can be empty mid-typing; parsed
  // on submit and dropped whenever the clinic covers the flight.
  flight_cost: z.string().optional().or(z.literal("")),
  // ArtDent 261019 — the part of the fare not on the clinic (FareSplit).
  // Blank = nobody split it: the whole fare is the payer's.
  flight_patient_amount: z.string().optional().or(z.literal("")),
  client_pays_hotel: z.boolean(),
  // Booking-specific note (room type, breakfast, late check-in). Lives beside
  // the stay, not in the general trip `notes`.
  hotel_notes: z.string().max(2000).optional().or(z.literal("")),
  // ArtDent 260922 — the hotel's own check-in/out wording, as the
  // accommodation mandate prints it. Free text, not a time.
  hotel_check_in_time: z.string().max(40).optional().or(z.literal("")),
  hotel_check_out_time: z.string().max(40).optional().or(z.literal("")),
  hotel_name: z.string().max(255).optional().or(z.literal("")),
  // Registry link. Null whenever the name is free text — the server copies a
  // linked hotel's name into hotel_name, so hotel_name is always the display
  // value and this is only the join.
  hotel_id: z.string().nullable().optional(),
  // Kept as a string so the field can be empty mid-typing; parsed on submit.
  hotel_cost: z.string().optional().or(z.literal("")),
  hotel_cost_mode: z.enum(["total", "per_night"]),
  check_in_date: z.string().optional().or(z.literal("")),
  check_out_date: z.string().optional().or(z.literal("")),
  // Companions riding along (spouse, family) — server caps at 19 so the
  // synced transfer legs stay inside their 1..20 passenger_count rule.
  extra_passengers: z.number().int().min(0).max(19),
  // Visit Purpose v2: flag-gated, NO_PURPOSE sentinel because a Radix Select
  // item can't be "".
  purpose: z.string().optional().or(z.literal("")),
  notes: z.string().max(5000).optional().or(z.literal("")),
}).refine((data) => !purposeRequired || (!!data.purpose && data.purpose !== NO_PURPOSE), {
  message: t("travelForm.purposeRequired"),
  path: ["purpose"],
}).refine((data) => {
  if (data.arrival_date && data.departure_date) {
    return new Date(data.arrival_date) <= new Date(data.departure_date)
  }
  return true
}, {
  message: t("travelForm.departureAfterArrival"),
  path: ["departure_date"],
}).refine((data) => {
  if (data.check_in_date && data.check_out_date) {
    return new Date(data.check_in_date) <= new Date(data.check_out_date)
  }
  return true
}, {
  message: t("travelForm.checkOutAfterCheckIn"),
  path: ["check_out_date"],
})

type TravelFormValues = z.infer<ReturnType<typeof createTravelSchema>>

interface TravelFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  leadId: string
  /** Header eyebrow: "{name} · Travel". Falls back to the plain section eyebrow. */
  leadName?: string | null
  /**
   * ArtDent 260922 — the lead's referral partner, when it has one. Present,
   * it is offered as a payer for the flight and the stay; absent, the picker
   * asks only clinic / patient (the server refuses "affiliator" either way).
   */
  leadAffiliator?: { name: string } | null
  travel?: LeadTravel | null
  /**
   * The patient's trips as the page holds them. They feed the repeated-stay
   * warning only — the same hotel over the same nights is refused by the
   * server whether or not this is passed.
   */
  existingTravels?: LeadTravel[]
  onSuccess?: () => void
  /**
   * ArtDent 261002 — the trip this sheet adds (or edits) an extra hotel stay
   * on. Present, the sheet is the accommodation block alone: a stay has no
   * flights, purpose, companions or notes of its own.
   */
  stayOf?: LeadTravel | null
  /**
   * What a NEW stay starts from — "add a night" copies the paid stay's hotel,
   * rate and payer. Keep the object stable while the sheet is open; a fresh
   * one re-seeds the form.
   */
  stayPrefill?: Partial<LeadTravel> | null
}

/** The trip's answers a stay never carries (ShapesHotelStays server-side). */
/** The coordinator's half of a trip (DentaDent 261020) — mirrors TripDeskFields::FIELDS. */
const DESK_ONLY_FIELDS = [
  "hotel_id", "hotel_name", "hotel_cost", "hotel_cost_mode", "hotel_cost_currency",
  "check_in_date", "check_out_date", "hotel_check_in_time", "hotel_check_out_time",
  "client_pays_hotel", "hotel_payer", "hotel_patient_nights", "hotel_notes",
  "hotel_declined", "accommodation_mandate", "notes",
] as const

const STAY_TRIP_ONLY_FIELDS = [
  "arrival_date", "arrival_time", "arrival_takeoff_time", "arrival_airport",
  "departure_date", "departure_time", "departure_landing_time", "departure_airport",
  "client_pays_flight", "flight_payer", "flight_cost", "flight_patient_amount",
  "extra_passengers", "purpose", "notes", "hotel_declined",
] as const

/** Stable default, so the warning's memo does not recompute on every render. */
const NO_TRAVELS: LeadTravel[] = []

/** Arranged by the clinic, none recorded, or declined by the patient. */
type AccommodationMode = "arranged" | "none" | "declined"

const EMPTY_VALUES: TravelFormValues = {
  arrival_date: "",
  arrival_time: "",
  arrival_takeoff_time: "",
  arrival_airport: "",
  origin_city: "",
  departure_date: "",
  departure_time: "",
  departure_landing_time: "",
  departure_airport: "",
  client_pays_flight: false,
  flight_payer: "clinic",
  hotel_payer: "clinic",
  hotel_patient_nights: "",
  flight_cost: "",
  flight_patient_amount: "",
  client_pays_hotel: false,
  hotel_notes: "",
  hotel_check_in_time: "",
  hotel_check_out_time: "",
  hotel_name: "",
  hotel_id: null,
  hotel_cost: "",
  // ArtDent 260922 — rates are quoted per night, so the picker opens there.
  hotel_cost_mode: "per_night",
  check_in_date: "",
  check_out_date: "",
  extra_passengers: 0,
  purpose: NO_PURPOSE,
  notes: "",
}

/** Note template chips — each inserts a labelled line into the note. */
const NOTE_CHIPS = ["airportPickup", "dietary", "interpreter", "visa"] as const

/**
 * Trip sheet (design "Add Trip Modal v3", 2026-08-26): a 720px sheet — purpose
 * + companions on top, flights beside accommodation on a shared column rhythm,
 * notes + voucher attachments on the bottom strip, and a live route summary in
 * the footer. Check-in/out MIRROR the flight dates until either is edited by
 * hand; the accommodation switch clears the hotel block server-side when off.
 */
export function TravelFormDialog({
  open,
  onOpenChange,
  leadId,
  leadName,
  leadAffiliator = null,
  travel,
  existingTravels = NO_TRAVELS,
  onSuccess,
  stayOf = null,
  stayPrefill = null,
}: TravelFormDialogProps) {
  const clinicFormat = useFormatter()
  const t = useTranslations("leadDetail")
  const tc = useTranslations("common")
  const tv = useTranslations("visitCategorization")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditing = !!travel
  const isStay = !!stayOf
  // A settled hotel bill freezes its hotel, nights and cost server-side
  // (LeadTravel::hotelBillLocked); the sheet says so instead of failing on save.
  const hotelLocked = !!travel?.hotel_locked

  const hotelsEnabled = useAuthStore(
    (state) => state.user?.feature_flags?.hotels_enabled ?? false
  )
  const { currency: clinicCurrency } = useCurrency()
  const { formatCurrency } = useFormatCurrency()
  // ArtDent 260918 — hotel rates by occupancy, to PREFILL the stay's
  // per-night cost. Loaded once per open; the combobox has its own copy, but
  // it only hands back an id, and an edited trip arrives with the id already
  // set, so the lookup lives here.
  const [rateHotels, setRateHotels] = useState<Hotel[]>([])
  // The last cost this dialog filled in itself: only THAT value (or an empty
  // field) is ever replaced when the hotel or the party changes. A cost the
  // desk typed is never overwritten.
  const lastAutoCostRef = useRef<string | null>(null)
  // The last night split this dialog filled in itself — same rule as the cost.
  const lastAutoNightsRef = useRef<string | null>(null)
  useEffect(() => {
    if (!open) {
      lastAutoCostRef.current = null
      lastAutoNightsRef.current = null
      return
    }
    if (!hotelsEnabled) return
    let cancelled = false
    getHotels({ activeOnly: true })
      .then((rows) => {
        if (!cancelled) setRateHotels(rows)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [open, hotelsEnabled])
  const visitCategorizationEnabled = useAuthStore(
    (state) => state.user?.feature_flags?.visit_categorization_enabled ?? false
  )
  // ArtDent 260922 — the three-way payers, the night split, the hotel's
  // check-in/out wording and "the patient declines a hotel". Off, the form
  // keeps its two yes/no toggles and the accommodation switch, and sends
  // none of the new columns (the request would strip them anyway).
  // DentaDent 261020 — Accommodation and Notes are the coordinator's: a pure
  // agent account does not see them, and its saves leave them as they are
  // (the server drops the same keys — TripDeskFields).
  const hotelNotesDeskOnly = useAuthStore((state) => {
    const roles = state.user?.roles ?? []
    return (
      (state.user?.feature_flags?.trip_hotel_notes_desk_only_enabled ?? false) &&
      roles.length > 0 &&
      roles.every((role) => hasRole([role], ROLES.AGENT))
    )
  })
  const travelPayersEnabled = useAuthStore(
    (state) => state.user?.feature_flags?.travel_payers_enabled ?? false
  )
  // Dentx 261016 — the mandate prints from the trip, so the night split is
  // two counts on screen (NightsSplit) instead of a payer and a hidden count.
  const nightsSplitEnabled = useAuthStore(
    (state) =>
      (state.user?.feature_flags?.travel_payers_enabled ?? false) &&
      (state.user?.feature_flags?.mandate_from_trip_enabled ?? false)
  )
  // Who carries the non-clinic nights while the count is 0 (the payer then
  // reads "clinic", so it cannot remember the choice itself).
  const [splitParty, setSplitParty] = useState<NightsParty>("patient")
  // ArtDent 261019 — the flight price split like the nights: a ticket price,
  // then the clinic's part and the patient's (or partner's), as amounts.
  const fareSplitEnabled = useAuthStore(
    (state) =>
      (state.user?.feature_flags?.travel_payers_enabled ?? false) &&
      (state.user?.feature_flags?.flight_cost_split_enabled ?? false)
  )
  // The same memory for the fare's other party while its part is 0.
  const [fareParty, setFareParty] = useState<FareParty>("patient")
  const travelCoordinationEnabled = useAuthStore(
    (state) => state.user?.feature_flags?.travel_coordination_enabled ?? false
  )
  // Radiance 261001 — DentaDent's ID and passport check, on its own: warn
  // while the trip is open when the patient has no ID card or passport on
  // file. A warning, never a block — the trip still saves.
  const identityDocumentsEnabled = useAuthStore(
    (state) => state.user?.feature_flags?.identity_documents_enabled ?? false
  )
  // Radiance 261001 — "Flying from": the city the patient flies from, typed
  // off the ticket for the desk's travel sheet. Off, the field is not shown
  // and not sent (the request would strip it anyway).
  const originCityEnabled = useAuthStore(
    (state) => state.user?.feature_flags?.travel_origin_city_enabled ?? false
  )
  // Radiance 261019 — "when the ticket goes in, add both times": each flight
  // gets a Departs AND an Arrives column. Off, the one Time column stays.
  const bothFlightTimes = useAuthStore(
    (state) => state.user?.feature_flags?.travel_flight_both_times_enabled ?? false
  )
  const [identityMissing, setIdentityMissing] = useState(false)

  useEffect(() => {
    if (!open || !identityDocumentsEnabled) return
    let cancelled = false
    setIdentityMissing(false)
    getIdentityDocumentStatus(leadId)
      .then((status) => {
        if (!cancelled) setIdentityMissing(!status.on_file)
      })
      .catch(() => {
        /* advisory only — a failed check shows nothing rather than a false alarm */
      })
    return () => {
      cancelled = true
    }
  }, [open, identityDocumentsEnabled, leadId])

  // ── Purpose suggestions (per-visit-ordinal map orders the dropdown) ──
  const [purposesMap, setPurposesMap] = useState<VisitPurposesMap>({})
  const [leadVisits, setLeadVisits] = useState<ClinicalVisit[]>([])

  useEffect(() => {
    if (!open || !visitCategorizationEnabled) return
    let cancelled = false
    Promise.all([getVisitPurposes(), getLeadVisits(leadId).catch(() => [])])
      .then(([purposes, visits]) => {
        if (cancelled) return
        setPurposesMap(purposes)
        setLeadVisits(visits)
      })
      .catch(() => {
        /* suggestions are cosmetic — the field still works ungrouped */
      })
    return () => {
      cancelled = true
    }
  }, [open, visitCategorizationEnabled, leadId])

  const suggestedOrdinal = useMemo(() => {
    if (travel) {
      const linked = leadVisits.find((visit) => visit.travel_id === travel.id)
      if (linked) return linked.visit_number
    }
    return leadVisits.length + 1
  }, [travel, leadVisits])

  const { suggestedPurposes, otherPurposes } = useMemo(() => {
    const all = Array.from(new Set(Object.values(purposesMap).flat()))
    if (travelCoordinationEnabled) return { suggestedPurposes: [], otherPurposes: all }
    const suggested = purposesMap[String(Math.min(suggestedOrdinal, 4))] ?? []
    return {
      suggestedPurposes: suggested,
      otherPurposes: all.filter((purpose) => !suggested.includes(purpose)),
    }
  }, [purposesMap, suggestedOrdinal, travelCoordinationEnabled])

  const purposeLabel = (key: string) =>
    travelCoordinationEnabled && t.has(`travelForm.purposes.${key}`)
      ? t(`travelForm.purposes.${key}`)
      : tv.has(`purposes.${key}`) ? tv(`purposes.${key}`) : key

  // ── Form ──
  const travelSchema = createTravelSchema(t, travelCoordinationEnabled && !isEditing)

  const form = useForm<TravelFormValues>({
    resolver: zodResolver(travelSchema),
    defaultValues: EMPTY_VALUES,
  })
  const { errors } = form.formState
  const arrivalDateForChecks = form.watch("arrival_date")
  const [travelChecks, setTravelChecks] = useState<TravelChecks | null>(null)
  const [checksLoading, setChecksLoading] = useState(false)
  const [checksFailed, setChecksFailed] = useState(false)

  useEffect(() => {
    if (!open || !travelCoordinationEnabled) return
    let cancelled = false
    setTravelChecks(null)
    setChecksLoading(true)
    setChecksFailed(false)
    getTravelChecks(leadId, arrivalDateForChecks)
      .then((checks) => {
        if (!cancelled) setTravelChecks(checks)
      })
      .catch(() => {
        if (!cancelled) setChecksFailed(true)
      })
      .finally(() => {
        if (!cancelled) setChecksLoading(false)
      })
    return () => { cancelled = true }
  }, [open, travelCoordinationEnabled, leadId, arrivalDateForChecks])

  // Accommodation block: switch + flight-date mirroring. Check-in/out follow
  // arrival/departure until either is hand-edited (design: "Matched to the
  // flight dates. Edit either to break the link.").
  // ArtDent 260922 — three answers, not a switch: the clinic arranges a stay,
  // there is none recorded, or the PATIENT DECLINES one (an answer the desk
  // must see, pinned as a front-desk note server-side).
  const [accommodationMode, setAccommodationMode] = useState<AccommodationMode>("arranged")
  const accommodationOn = accommodationMode === "arranged"
  const [datesLinked, setDatesLinked] = useState(true)

  // ── Attachments ──
  const [attachments, setAttachments] = useState<AttachmentDocument[]>([])
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const attachmentsBase = travel ? `/travel/${travel.id}/documents` : null

  useEffect(() => {
    if (!open) return
    setPendingFiles([])
    if (travel) {
      getAttachments(`/travel/${travel.id}/documents`)
        .then(setAttachments)
        .catch(() => setAttachments([]))
    } else {
      setAttachments([])
    }
  }, [open, travel])

  // Seed / reset on open.
  useEffect(() => {
    if (!open) return
    if (travel) {
      form.reset({
        arrival_date: travel.arrival_date || "",
        arrival_time: travel.arrival_time || "",
        arrival_takeoff_time: travel.arrival_takeoff_time || "",
        arrival_airport: travel.arrival_airport || "",
        origin_city: travel.origin_city || "",
        departure_date: travel.departure_date || "",
        departure_time: travel.departure_time || "",
        departure_landing_time: travel.departure_landing_time || "",
        departure_airport: travel.departure_airport || "",
        client_pays_flight: travel.client_pays_flight ?? false,
        // A trip from before the payers existed answers through its boolean.
        flight_payer: travel.flight_payer ?? (travel.client_pays_flight ? "patient" : "clinic"),
        hotel_payer: travel.hotel_payer ?? (travel.client_pays_hotel ? "patient" : "clinic"),
        hotel_patient_nights:
          travel.hotel_patient_nights != null ? String(travel.hotel_patient_nights) : "",
        flight_cost: travel.flight_cost != null ? String(travel.flight_cost) : "",
        flight_patient_amount:
          travel.flight_patient_amount != null ? String(travel.flight_patient_amount) : "",
        client_pays_hotel: travel.client_pays_hotel ?? false,
        hotel_notes: travel.hotel_notes || "",
        hotel_check_in_time: travel.hotel_check_in_time || "",
        hotel_check_out_time: travel.hotel_check_out_time || "",
        hotel_name: travel.hotel_name || "",
        hotel_id: travel.hotel_id ?? null,
        hotel_cost: travel.hotel_cost != null ? String(travel.hotel_cost) : "",
        hotel_cost_mode: travel.hotel_cost_mode ?? "per_night",
        check_in_date: travel.check_in_date || "",
        check_out_date: travel.check_out_date || "",
        // A stay prices the room for the trip's party.
        extra_passengers: (stayOf ?? travel).extra_passengers ?? 0,
        purpose: travel.purpose ?? NO_PURPOSE,
        notes: travel.notes || "",
      })
      setAccommodationMode(
        travel.hotel_declined
          ? "declined"
          : travel.hotel_name ||
              travel.check_in_date ||
              travel.check_out_date ||
              travel.client_pays_hotel ||
              travel.hotel_notes
            ? "arranged"
            : "none"
      )
      setDatesLinked(
        !stayOf &&
          (travel.check_in_date || "") === (travel.arrival_date || "") &&
          (travel.check_out_date || "") === (travel.departure_date || "")
      )
    } else if (stayOf) {
      const from = stayPrefill ?? {}
      form.reset({
        ...EMPTY_VALUES,
        extra_passengers: stayOf.extra_passengers ?? 0,
        hotel_id: from.hotel_id ?? null,
        hotel_name: from.hotel_name || "",
        check_in_date: from.check_in_date || "",
        check_out_date: from.check_out_date || "",
        hotel_cost: from.hotel_cost != null ? String(from.hotel_cost) : "",
        hotel_cost_mode: from.hotel_cost_mode ?? "per_night",
        hotel_payer: from.hotel_payer ?? "clinic",
        hotel_check_in_time: from.hotel_check_in_time || "",
        hotel_check_out_time: from.hotel_check_out_time || "",
      })
      setAccommodationMode("arranged")
      setDatesLinked(false)
    } else {
      form.reset(EMPTY_VALUES)
      setAccommodationMode("arranged")
      setDatesLinked(true)
    }
  }, [open, travel, form, stayOf, stayPrefill])

  const setFlightDate = (
    field: "arrival_date" | "departure_date",
    value: string,
  ) => {
    form.setValue(field, value, { shouldValidate: true })
    if (datesLinked && accommodationOn) {
      form.setValue(
        field === "arrival_date" ? "check_in_date" : "check_out_date",
        value,
        { shouldValidate: true },
      )
    }
  }

  const setStayDate = (
    field: "check_in_date" | "check_out_date",
    value: string,
  ) => {
    setDatesLinked(false)
    form.setValue(field, value, { shouldValidate: true })
  }

  // ── Attachment handlers ──
  const addFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return
      if (!attachmentsBase) {
        // Create mode: queue locally, upload after the trip exists.
        setPendingFiles((previous) => [...previous, ...files])
        return
      }
      setIsUploading(true)
      try {
        for (const file of files) {
          const uploaded = await uploadAttachment(attachmentsBase, file)
          setAttachments((previous) => [...previous, uploaded])
        }
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } } }
        toast.error(err.response?.data?.message || t("travelForm.toast.uploadFailed"))
      } finally {
        setIsUploading(false)
      }
    },
    [attachmentsBase, t],
  )

  const removeAttachment = async (media: AttachmentDocument) => {
    if (!attachmentsBase) return
    try {
      await deleteAttachment(attachmentsBase, media.id)
      setAttachments((previous) => previous.filter((item) => item.id !== media.id))
    } catch {
      toast.error(t("travelForm.toast.removeFailed"))
    }
  }

  // ── Submit ──
  const onSubmit = async (values: TravelFormValues) => {
    setIsSubmitting(true)
    try {
      // Empty string → explicit null so clearing a field actually clears it
      // server-side (a nulled flight date deletes/cancels its transfer leg).
      const cleanData = Object.fromEntries(
        Object.entries(values).map(([key, value]) => [
          key,
          value === "" ? null : value,
        ])
      ) as CreateLeadTravelData

      // ArtDent 260922 — the payer is what the picker set; the booleans the
      // rest of the fleet reads are derived from it server-side, so they are
      // sent in step here too rather than left to whatever the form loaded.
      cleanData.client_pays_flight = values.flight_payer === "patient"
      cleanData.client_pays_hotel = values.hotel_payer === "patient"

      if (fareSplitEnabled) {
        // ArtDent 261019 — the fare is the whole ticket, whoever pays, and
        // the split says how much of it is not the clinic's. Without a price
        // there is nothing to split, so the payer stays as it was (a trip
        // marked "patient pays" before the price is known keeps saying so).
        const fare = parseFareInput(values.flight_cost)
        const typedShare = parseFareInput(values.flight_patient_amount)
        const share = fare === null ? null : fareOtherShare(fare, values.flight_payer, typedShare)
        const payer = share === 0 ? "clinic" : values.flight_payer
        cleanData.flight_cost = fare
        cleanData.flight_payer = payer
        cleanData.client_pays_flight = payer === "patient"
        cleanData.flight_patient_amount = payer === "clinic" ? 0 : share
      } else if (values.flight_payer !== "clinic") {
        // The fare only exists when someone other than the clinic pays the
        // flight; an empty box clears it.
        const typedFare = (values.flight_cost ?? "").trim()
        const fare = typedFare === "" ? null : Number(typedFare)
        cleanData.flight_cost = fare !== null && Number.isFinite(fare) ? fare : null
      } else {
        cleanData.flight_cost = null
      }
      if (!fareSplitEnabled) {
        delete cleanData.flight_patient_amount
      }

      // The night split only exists when someone other than the clinic pays
      // the stay. Clamped to the stay when the dates are known; an empty box
      // means all of it. Clinic-paid = 0, never null, so the report's split
      // stops reading "unknown" once a payer has been chosen.
      if (values.hotel_payer !== "clinic") {
        const typedNights = (values.hotel_patient_nights ?? "").trim()
        const wanted = typedNights === "" ? null : parseInt(typedNights, 10)
        const all = stayNights ?? null
        const nights = wanted === null || !Number.isFinite(wanted) ? all : wanted
        cleanData.hotel_patient_nights =
          nights === null ? null : Math.max(0, all === null ? nights : Math.min(nights, all))
      } else {
        cleanData.hotel_patient_nights = 0
      }

      cleanData.hotel_declined = accommodationMode === "declined"

      if (!travelPayersEnabled) {
        // The booleans above carry the answer, the way they always did.
        delete cleanData.flight_payer
        delete cleanData.hotel_payer
        delete cleanData.hotel_declined
        delete cleanData.hotel_patient_nights
        delete cleanData.hotel_check_in_time
        delete cleanData.hotel_check_out_time
      }

      if (!accommodationOn) {
        cleanData.client_pays_hotel = false
        cleanData.hotel_payer = "clinic"
        cleanData.hotel_patient_nights = null
        cleanData.hotel_notes = null
        cleanData.hotel_check_in_time = null
        cleanData.hotel_check_out_time = null
        cleanData.hotel_name = null
        cleanData.hotel_id = null
        cleanData.hotel_cost = null
        cleanData.check_in_date = null
        cleanData.check_out_date = null
      }

      if (hotelsEnabled) {
        // An empty box means "no cost recorded", which must clear the stored
        // one rather than send "". The mode only travels with an amount.
        const typed = (values.hotel_cost ?? "").trim()
        const amount = typed === "" ? null : Number(typed)
        cleanData.hotel_cost = amount !== null && Number.isFinite(amount) ? amount : null
        cleanData.hotel_cost_mode = cleanData.hotel_cost === null ? null : values.hotel_cost_mode
      } else {
        delete cleanData.hotel_id
        delete cleanData.hotel_cost
        delete cleanData.hotel_cost_mode
      }

      if (visitCategorizationEnabled) {
        const purpose = values.purpose?.trim()
        cleanData.purpose = purpose && purpose !== NO_PURPOSE ? purpose : null
      } else {
        delete cleanData.purpose
      }

      if (originCityEnabled) {
        cleanData.origin_city = values.origin_city?.trim() || null
      } else {
        delete cleanData.origin_city
      }

      if (!bothFlightTimes) {
        delete cleanData.arrival_takeoff_time
        delete cleanData.departure_landing_time
      }

      if (hotelNotesDeskOnly) {
        // Wider than the form's payload type (mandate, currency) on purpose:
        // it mirrors the server's list, which is the rule.
        for (const field of DESK_ONLY_FIELDS) delete (cleanData as Record<string, unknown>)[field]
      }

      if (stayOf) {
        for (const field of STAY_TRIP_ONLY_FIELDS) delete cleanData[field]
        // A stay is the hotel half of the trip; where they fly from is not its.
        delete cleanData.origin_city
        if (!isEditing) cleanData.parent_travel_id = stayOf.id
      }

      let saved: LeadTravel
      if (isEditing && travel) {
        saved = await updateLeadTravel(leadId, travel.id, cleanData)
        toast.success(t(isStay ? "travelForm.toast.stayUpdated" : "travelForm.toast.updated"))
      } else if (isStay) {
        saved = await createLeadTravel(leadId, cleanData)
        toast.success(t("travelForm.toast.stayCreated"))
      } else {
        saved = await createLeadTravel(leadId, cleanData)
        // The trip now exists — flush the queued vouchers onto it.
        for (const file of pendingFiles) {
          try {
            await uploadAttachment(`/travel/${saved.id}/documents`, file)
          } catch {
            toast.error(t("travelForm.toast.uploadFailedNamed", { name: file.name }))
          }
        }
        toast.success(t("travelForm.toast.created"))
      }

      onSuccess?.()
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string; errors?: Record<string, string[]> } }
      }
      const data = err.response?.data
      // The reason lives under `errors`; `message` on a 422 is only ever the
      // generic "Validation failed".
      const reason = data?.errors ? Object.values(data.errors)[0]?.[0] : undefined
      setServerStayError(data?.errors?.hotel_name?.[0] ?? null)
      toast.error(reason ?? data?.message ?? t("travelForm.toast.saveFailed"))
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Derived display ──
  const [
    arrivalDate,
    departureDate,
    arrivalTime,
    departureTime,
    arrivalAirport,
    departureAirport,
    extraPassengers,
    purposeValue,
    arrivalTakeoffTime,
    departureLandingTime,
  ] = form.watch([
    "arrival_date",
    "departure_date",
    "arrival_time",
    "departure_time",
    "arrival_airport",
    "departure_airport",
    "extra_passengers",
    "purpose",
    "arrival_takeoff_time",
    "departure_landing_time",
  ])

  // A stay shows the window of the trip it belongs to.
  const windowArrival = stayOf ? stayOf.arrival_date ?? "" : arrivalDate
  const windowDeparture = stayOf ? stayOf.departure_date ?? "" : departureDate
  const nights =
    windowArrival && windowDeparture
      ? differenceInCalendarDays(new Date(windowDeparture), new Date(windowArrival))
      : null

  // Cost preview. Deliberately NOT the `nights` above: that one spans the
  // FLIGHT dates, while a stay's cost is derived from check-in/check-out —
  // the same rule LeadTravel::nights() applies server-side, so the hint here
  // and the number in the report can never disagree.
  const [costAmountRaw, costMode, checkInDate, checkOutDate] = form.watch([
    "hotel_cost",
    "hotel_cost_mode",
    "check_in_date",
    "check_out_date",
  ])

  const [flightPayer, hotelPayer] = form.watch(["flight_payer", "hotel_payer"])

  // The fare's split as FareSplit shows it — the same rule the save applies.
  const [typedFare, typedFareShare] = form.watch(["flight_cost", "flight_patient_amount"])
  const fareValue = parseFareInput(typedFare)
  const fareOtherAmount =
    fareValue === null ? 0 : fareOtherShare(fareValue, flightPayer, parseFareInput(typedFareShare))

  const stayNights = (() => {
    if (!checkInDate || !checkOutDate) return null
    const n = differenceInCalendarDays(new Date(checkOutDate), new Date(checkInDate))
    return n >= 1 ? n : null
  })()

  // ArtDent 260922 — the night split FOLLOWS the stay. When somebody other
  // than the clinic pays, the count starts as the whole stay and moves with
  // the check-in/out dates for as long as it still equals what this form
  // filled in itself; a count the desk typed is never overwritten. Same
  // shape as the auto-filled hotel rate above. Only ever runs with the
  // travel_payers flag, which is the only time the field is shown.
  useEffect(() => {
    // NightsSplit (Dentx) starts every night on the clinic and never fills in.
    if (!travelPayersEnabled || !open || nightsSplitEnabled) return
    if (hotelPayer === "clinic" || stayNights === null) return
    const current = (form.getValues("hotel_patient_nights") ?? "").trim()
    if (current !== "" && current !== lastAutoNightsRef.current) return
    const next = String(stayNights)
    if (current === next) return
    form.setValue("hotel_patient_nights", next, { shouldDirty: true })
    lastAutoNightsRef.current = next
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelPayersEnabled, open, hotelPayer, stayNights, nightsSplitEnabled])

  // The split as NightsSplit shows it: a blank count on a non-clinic payer
  // means the whole stay, exactly as the save below reads it.
  const typedPatientNights = (form.watch("hotel_patient_nights") ?? "").trim()
  const splitPatientNights =
    hotelPayer === "clinic"
      ? 0
      : typedPatientNights === ""
        ? (stayNights ?? 0)
        : parseInt(typedPatientNights, 10) || 0

  // ArtDent 260918 — the hotel's rate for this party (patient + companions):
  // exact occupancy, else the largest priced occupancy at or below it — the
  // same rule as Hotel::rateFor server-side.
  const watchedHotelId = form.watch("hotel_id")
  const watchedHotelName = form.watch("hotel_name")

  // One patient, one hotel, one set of nights — never entered twice. Warned
  // here while the hotel is still being chosen; refused by the server
  // (HotelStayGuard) either way.
  const stayConflict = useMemo(() => {
    if (!hotelsEnabled || !accommodationOn) return null

    const candidate = {
      id: travel?.id ?? null,
      hotel_id: watchedHotelId ?? null,
      hotel_name: watchedHotelName ?? "",
      check_in_date: checkInDate || null,
      check_out_date: checkOutDate || null,
      arrival_date: arrivalDate || null,
      departure_date: departureDate || null,
    }

    // Same as the server: an edit that leaves the hotel and the nights where
    // they were is never refused, even on a trip that was already a repeat.
    if (travel) {
      const day = (value?: string | null) => (value ?? "").slice(0, 10)
      const unmoved =
        (travel.hotel_id ?? null) === candidate.hotel_id &&
        (travel.hotel_name ?? "").trim() === candidate.hotel_name.trim() &&
        day(travel.check_in_date) === day(candidate.check_in_date) &&
        day(travel.check_out_date) === day(candidate.check_out_date) &&
        day(travel.arrival_date) === day(candidate.arrival_date) &&
        day(travel.departure_date) === day(candidate.departure_date)
      if (unmoved) return null
    }

    return findHotelStayConflict(candidate, existingTravels)
  }, [
    hotelsEnabled,
    accommodationOn,
    travel,
    existingTravels,
    watchedHotelId,
    watchedHotelName,
    checkInDate,
    checkOutDate,
    arrivalDate,
    departureDate,
  ])

  const stayConflictMessage = (() => {
    if (!stayConflict) return null
    // The name as the existing stay carries it, not as it was just typed.
    const hotel = stayConflict.hotel_name?.trim() || (watchedHotelName ?? "").trim()
    const from = stayConflict.check_in_date ?? stayConflict.arrival_date
    const to = stayConflict.check_out_date ?? stayConflict.departure_date
    const show = (value: string) => clinicFormat.dateTime(new Date(`${value.slice(0, 10)}T00:00:00`), { dateStyle: "medium" })

    if (!from && !to) return t("travelForm.stayConflictUndated", { hotel })
    if (!from || !to) return t("travelForm.stayConflictOn", { hotel, date: show((from ?? to) as string) })
    return t("travelForm.stayConflict", { hotel, from: show(from), to: show(to) })
  })()

  // What the server said about this hotel on the last save. It goes stale the
  // moment the hotel or the dates move, so it is dropped then.
  const [serverStayError, setServerStayError] = useState<string | null>(null)
  useEffect(() => {
    setServerStayError(null)
  }, [open, watchedHotelId, watchedHotelName, checkInDate, checkOutDate, arrivalDate, departureDate])

  const partySize = 1 + (extraPassengers ?? 0)
  const rateSuggestion = (() => {
    if (!hotelsEnabled || !watchedHotelId) return null
    const hotel = rateHotels.find((h) => h.id === watchedHotelId)
    const rates = hotel?.rates ?? []
    if (!hotel || rates.length === 0) return null
    const rate =
      rates.find((r) => r.persons === partySize) ??
      [...rates].filter((r) => r.persons <= partySize).sort((a, b) => b.persons - a.persons)[0]
    return rate ? { hotel, rate } : null
  })()
  useEffect(() => {
    if (!rateSuggestion) return
    const current = (form.getValues("hotel_cost") ?? "").trim()
    if (current !== "" && current !== lastAutoCostRef.current) return
    const next = String(rateSuggestion.rate.price_per_night)
    if (current === next && form.getValues("hotel_cost_mode") === "per_night") return
    form.setValue("hotel_cost", next, { shouldDirty: true })
    form.setValue("hotel_cost_mode", "per_night", { shouldDirty: true })
    lastAutoCostRef.current = next
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateSuggestion?.hotel.id, rateSuggestion?.rate.id])
  const rateApplied =
    rateSuggestion !== null &&
    (costAmountRaw ?? "").trim() === lastAutoCostRef.current &&
    costMode === "per_night"

  const costHint = (() => {
    const amount = Number((costAmountRaw ?? "").trim())
    if (!(costAmountRaw ?? "").trim() || !Number.isFinite(amount) || amount <= 0) return null

    if (stayNights === null) {
      // A per-night price with no nights has no total — the expenses report
      // excludes exactly this stay, so say so while it can still be fixed.
      return costMode === "per_night"
        ? t("travelForm.hotelCostNeedsDates")
        : t("travelForm.hotelCostNoNights")
    }

    return costMode === "per_night"
      ? t("travelForm.hotelCostHintPerNight", {
          nights: stayNights,
          amount: formatCurrency(amount * stayNights),
        })
      : t("travelForm.hotelCostHintTotal", {
          nights: stayNights,
          amount: formatCurrency(amount / stayNights),
        })
  })()

  const tripWindow = windowArrival && windowDeparture
    ? `${formatDateFns(new Date(windowArrival), "d")} → ${clinicFormat.dateTime(new Date(windowDeparture), { year: "numeric", month: "short", day: "numeric" })}`
    : windowArrival
      ? clinicFormat.dateTime(new Date(windowArrival), { year: "numeric", month: "short", day: "numeric" })
      : null

  const summaryLegs = [
    arrivalDate
      ? [arrivalAirport?.trim(), clinicFormat.dateTime(new Date(arrivalDate), { month: "short", day: "numeric" })]
          .filter(Boolean)
          .join(" ")
      : null,
    departureDate
      ? [departureAirport?.trim(), clinicFormat.dateTime(new Date(departureDate), { month: "short", day: "numeric" })]
          .filter(Boolean)
          .join(" ")
      : null,
  ].filter(Boolean) as string[]

  const fileCount = attachments.length + pendingFiles.length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0", isStay ? "sm:max-w-[480px]" : "sm:max-w-[820px]")}
        // A stay opens on the hotel picker, whose list would cover the cost
        // and dates the desk came to check; nothing is focused instead.
        onOpenAutoFocus={isStay ? (event) => event.preventDefault() : undefined}
      >
        {/* Header — eyebrow + serif title, trip window on the right */}
        <div className="flex shrink-0 items-start justify-between gap-5 px-6 pb-3.5 pt-5">
          <div>
            <div className="font-mono text-[9.5px] font-medium uppercase tracking-[0.13em] text-text-dim">
              {leadName
                ? t("travelForm.eyebrowNamed", { name: leadName })
                : t("travel.eyebrow")}
            </div>
            <DialogTitle asChild>
              <h2 className="mt-1.5 font-serif text-[25px] font-normal leading-[1.05] tracking-[-0.01em]">
                {isStay
                  ? isEditing ? t("travelForm.editStayTitle") : t("travelForm.addStayTitle")
                  : isEditing ? t("travelForm.editTitle") : t("travelForm.addTitle")}
              </h2>
            </DialogTitle>
          </div>
          {tripWindow && (
            <div className="pr-8 pt-0.5 text-right">
              <div className="mb-1 text-[11px] font-medium text-muted-foreground">
                {t("travelForm.tripWindow")}
              </div>
              <div className="font-mono text-[13px]">
                {tripWindow}
                {nights !== null && nights > 0 && (
                  <span className="text-text-dim">
                    {" · "}
                    {t("travelForm.nights", { count: nights })}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <form className="flex min-h-0 flex-1 flex-col" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-5">
            {/* Purpose + companions */}
            {!isStay && (
            <div
              className={cn(
                "grid gap-3.5",
                visitCategorizationEnabled
                  ? "grid-cols-1 sm:grid-cols-[1fr_196px]"
                  : "grid-cols-[196px]",
              )}
            >
              {visitCategorizationEnabled && (
                <div>
                  <FieldLabel>{t("travelForm.purpose")}</FieldLabel>
                  {/* DentaDent travel coordination: purpose is required and labelled
                      per clinic, so it keeps its own picker; everyone else gets the
                      shared one with the free-text "Other…". */}
                  {travelCoordinationEnabled ? (
                    <>
                      <Select
                        value={form.watch("purpose") || NO_PURPOSE}
                        onValueChange={(value) => form.setValue("purpose", value)}
                      >
                        <SelectTrigger className="h-[34px] w-full">
                          <SelectValue placeholder={t("travelForm.selectPurpose")} />
                        </SelectTrigger>
                        <SelectContent>
                          {(!travelCoordinationEnabled || (isEditing && !travel?.purpose)) && <SelectItem value={NO_PURPOSE}>
                            <span className="text-muted-foreground">
                              {t("travelForm.noPurpose")}
                            </span>
                          </SelectItem>}
                          {travel?.purpose && ![...suggestedPurposes, ...otherPurposes].includes(travel.purpose) && (
                            <SelectItem value={travel.purpose}>{purposeLabel(travel.purpose)}</SelectItem>
                          )}
                          {suggestedPurposes.length > 0 && (
                            <SelectGroup>
                              <SelectLabel>{t("travelForm.suggestedPurposes")}</SelectLabel>
                              {suggestedPurposes.map((purpose) => (
                                <SelectItem key={purpose} value={purpose}>
                                  {purposeLabel(purpose)}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                          {otherPurposes.length > 0 && (
                            <SelectGroup>
                              {!travelCoordinationEnabled && <SelectLabel>{t("travelForm.otherPurposes")}</SelectLabel>}
                              {otherPurposes.map((purpose) => (
                                <SelectItem key={purpose} value={purpose}>
                                  {purposeLabel(purpose)}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                        </SelectContent>
                      </Select>
                      {errors.purpose && <p className="mt-1 text-xs text-destructive">{errors.purpose.message}</p>}
                    </>
                  ) : (
                    <VisitPurposeSelect
                      value={purposeValue === NO_PURPOSE ? "" : purposeValue ?? ""}
                      onChange={(value) => form.setValue("purpose", value || NO_PURPOSE)}
                      suggested={suggestedPurposes}
                      others={otherPurposes}
                      groupLabels={{
                        suggested: t("travelForm.suggestedPurposes"),
                        other: t("travelForm.otherPurposes"),
                      }}
                      triggerClassName="h-[34px]"
                    />
                  )}
                </div>
              )}

              <div>
                <FieldLabel>{t("travelForm.companions")}</FieldLabel>
                <div className="flex items-center gap-1.5">
                  <div className="flex h-[34px] flex-1 gap-0.5 rounded-[9px] border border-border bg-surface-2 p-0.5">
                    {[0, 1, 2].map((count) => (
                      <button
                        key={count}
                        type="button"
                        aria-pressed={extraPassengers === count}
                        onClick={() =>
                          form.setValue("extra_passengers", count, { shouldValidate: true })
                        }
                        className={cn(
                          "flex-1 rounded-[7px] px-2 font-sans text-[12px] font-medium transition-colors",
                          extraPassengers === count
                            ? "bg-surface text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {count}
                      </button>
                    ))}
                    <button
                      type="button"
                      aria-pressed={extraPassengers >= 3}
                      onClick={() =>
                        form.setValue("extra_passengers", 3, { shouldValidate: true })
                      }
                      className={cn(
                        "flex-1 rounded-[7px] px-2 font-sans text-[12px] font-medium transition-colors",
                        extraPassengers >= 3
                          ? "bg-surface text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      3+
                    </button>
                  </div>
                  {extraPassengers >= 3 && (
                    <Input
                      type="number"
                      min={3}
                      max={19}
                      inputMode="numeric"
                      className="h-[34px] w-14 shrink-0 px-2 font-mono text-[13px]"
                      value={extraPassengers}
                      onChange={(event) =>
                        form.setValue(
                          "extra_passengers",
                          Math.max(3, Math.min(19, Number(event.target.value) || 3)),
                          { shouldValidate: true },
                        )
                      }
                    />
                  )}
                </div>
              </div>
            </div>

            )}

            {travelCoordinationEnabled && !isStay && (
              <div className="mt-4 space-y-2 text-sm" aria-live="polite">
                {checksLoading && <p className="text-muted-foreground">{t("travelForm.checking")}</p>}
                {checksFailed && <p role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-950 dark:bg-amber-950 dark:text-amber-100">{t("travelForm.checksFailed")}</p>}
                {travelChecks?.over_capacity && (
                  <p role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-950 dark:bg-amber-950 dark:text-amber-100">
                    {t("travelForm.capacityWarning", { count: travelChecks.clinic_visits ?? 0 })}
                  </p>
                )}
                {travelChecks?.missing_identity_document && (
                  <p role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-950 dark:bg-amber-950 dark:text-amber-100">{t("travelForm.identityWarning")}</p>
                )}
                {travelChecks && !travelChecks.coordinator_available && (!accommodationOn || !form.watch("hotel_name")?.trim()) && (
                  <p role="alert" className="rounded-md border border-destructive p-3 text-destructive">{t("travelForm.noCoordinatorWarning")}</p>
                )}
              </div>
            )}

            {/* Flights | Accommodation */}
            <div className={cn("grid grid-cols-1 gap-y-5", isStay ? "mt-1" : hotelNotesDeskOnly ? "mt-4" : "mt-4 sm:grid-cols-[1fr_1px_268px] sm:gap-x-[22px]")}>
              {/* Flights */}
              {!isStay && (
              <div className="grid content-start gap-2.5">
                <div className="flex min-h-[22px] items-center pb-0.5">
                  <BlockTitle>{t("travelForm.flights")}</BlockTitle>
                </div>
                {/* Radiance 261001 — where the patient flies FROM, off the
                    ticket, on the same label column as the two legs below. */}
                {originCityEnabled && (
                  <div className="grid grid-cols-[74px_1fr] items-center gap-x-2.5">
                    <span className="flex items-center gap-1.5 text-[12.5px]">
                      <IconMapPin className="size-3.5 text-text-dim" />
                      {t("travelForm.originCity")}
                    </span>
                    {/* Dentx 261016 — suggests the cities already typed. */}
                    <CityCombobox
                      value={form.watch("origin_city") ?? ""}
                      onChange={(city) => form.setValue("origin_city", city, { shouldDirty: true })}
                      placeholder={t("travelForm.originCityPlaceholder")}
                    />
                  </div>
                )}
                {/* Radiance 261019 — with both times, the Time column becomes
                    Departs | Arrives, in flying order on both rows; on a phone
                    the airport drops under the date to make room. */}
                <div
                  className={cn(
                    "grid items-center gap-y-2",
                    bothFlightTimes
                      ? "grid-cols-[74px_minmax(0,1fr)_56px_56px] gap-x-2 sm:grid-cols-[74px_1fr_62px_62px_84px] sm:gap-x-2.5"
                      : "grid-cols-[74px_1fr_82px_96px] gap-x-2.5",
                  )}
                >
                  <ColHead>{t("travelForm.leg")}</ColHead>
                  <ColHead>{t("travelForm.date")}</ColHead>
                  {bothFlightTimes ? (
                    <>
                      <ColHead>{t("travelForm.departs")}</ColHead>
                      <ColHead>{t("travelForm.arrives")}</ColHead>
                      <ColHead className="hidden sm:block">{t("travelForm.airport")}</ColHead>
                    </>
                  ) : (
                    <>
                      <ColHead>{t("travelForm.time")}</ColHead>
                      <ColHead>{t("travelForm.airport")}</ColHead>
                    </>
                  )}

                  <span className="flex items-center gap-1.5 text-[12.5px]">
                    <IconPlaneArrival className="size-3.5 text-text-dim" />
                    {t("travel.arrival")}
                  </span>
                  <DateField
                    value={arrivalDate}
                    onChange={(value) => setFlightDate("arrival_date", value)}
                    placeholder={t("travelForm.pickDate")}
                  />
                  {bothFlightTimes && (
                    <FlightTimeField
                      value={arrivalTakeoffTime}
                      onChange={(value) =>
                        form.setValue("arrival_takeoff_time", value, {
                          shouldDirty: true,
                          shouldValidate: !!errors.arrival_takeoff_time,
                        })
                      }
                      label={`${t("travel.arrival")} · ${t("travelForm.departs")}`}
                      invalid={!!errors.arrival_takeoff_time}
                      tight
                    />
                  )}
                  <FlightTimeField
                    value={arrivalTime}
                    onChange={(value) =>
                      form.setValue("arrival_time", value, {
                        shouldDirty: true,
                        shouldValidate: !!errors.arrival_time,
                      })
                    }
                    label={`${t("travel.arrival")} · ${t(bothFlightTimes ? "travelForm.arrives" : "travelForm.time")}`}
                    invalid={!!errors.arrival_time}
                    tight={bothFlightTimes}
                  />
                  <Input
                    className={cn(
                      "h-[34px] font-mono text-[13px] placeholder:text-text-dim/60",
                      bothFlightTimes && "col-span-3 col-start-2 sm:col-span-1 sm:col-start-auto",
                    )}
                    placeholder="IST"
                    aria-label={`${t("travel.arrival")} · ${t("travelForm.airport")}`}
                    {...form.register("arrival_airport")}
                  />

                  <span className="flex items-center gap-1.5 text-[12.5px]">
                    <IconPlaneDeparture className="size-3.5 text-text-dim" />
                    {t("travel.departure")}
                  </span>
                  <DateField
                    value={departureDate}
                    onChange={(value) => setFlightDate("departure_date", value)}
                    placeholder={t("travelForm.pickDate")}
                  />
                  <FlightTimeField
                    value={departureTime}
                    onChange={(value) =>
                      form.setValue("departure_time", value, {
                        shouldDirty: true,
                        shouldValidate: !!errors.departure_time,
                      })
                    }
                    label={`${t("travel.departure")} · ${t(bothFlightTimes ? "travelForm.departs" : "travelForm.time")}`}
                    invalid={!!errors.departure_time}
                    tight={bothFlightTimes}
                  />
                  {bothFlightTimes && (
                    <FlightTimeField
                      value={departureLandingTime}
                      onChange={(value) =>
                        form.setValue("departure_landing_time", value, {
                          shouldDirty: true,
                          shouldValidate: !!errors.departure_landing_time,
                        })
                      }
                      label={`${t("travel.departure")} · ${t("travelForm.arrives")}`}
                      invalid={!!errors.departure_landing_time}
                      tight
                    />
                  )}
                  <Input
                    className={cn(
                      "h-[34px] font-mono text-[13px] placeholder:text-text-dim/60",
                      bothFlightTimes && "col-span-3 col-start-2 sm:col-span-1 sm:col-start-auto",
                    )}
                    placeholder="JFK"
                    aria-label={`${t("travel.departure")} · ${t("travelForm.airport")}`}
                    {...form.register("departure_airport")}
                  />
                </div>
                {errors.departure_date && (
                  <p className="text-[11.5px] text-destructive">
                    {errors.departure_date.message}
                  </p>
                )}
                {(errors.arrival_time || errors.departure_time || errors.arrival_takeoff_time || errors.departure_landing_time) && (
                  <p className="text-[11.5px] text-destructive">
                    {(errors.arrival_takeoff_time ?? errors.arrival_time ?? errors.departure_time ?? errors.departure_landing_time)?.message}
                  </p>
                )}
                <p className="text-[11.5px] text-text-dim">
                  {t("travelForm.airportsHint")}
                </p>
                {fareSplitEnabled ? (
                  <div className="grid gap-2.5">
                    <div className="w-40">
                      <FieldLabel>{t("travelForm.ticketPrice")}</FieldLabel>
                      <div className="relative">
                        <Input
                          inputMode="decimal"
                          aria-label={t("travelForm.ticketPrice")}
                          className="h-[34px] pr-8 font-mono text-[13px]"
                          {...form.register("flight_cost")}
                        />
                        <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-[12px] text-text-dim">
                          {getCurrencySymbol(clinicCurrency)}
                        </span>
                      </div>
                    </div>
                    {fareValue !== null && fareValue > 0 ? (
                      <div>
                        <FieldLabel>{t("travelForm.ticketPaidBy")}</FieldLabel>
                        <FareSplit
                          fare={fareValue}
                          otherAmount={fareOtherAmount}
                          party={flightPayer === "clinic" ? fareParty : flightPayer}
                          partner={leadAffiliator ?? null}
                          onChange={({ otherAmount, party }) => {
                            setFareParty(party)
                            form.setValue("flight_patient_amount", String(otherAmount), { shouldDirty: true })
                            form.setValue("flight_payer", otherAmount > 0 ? party : "clinic", { shouldDirty: true })
                          }}
                          currencySymbol={getCurrencySymbol(clinicCurrency)}
                          labels={{
                            clinic: t("travelForm.payerClinic"),
                            patient: t("travelForm.payerPatient"),
                            partner: t("travelForm.payerPartner"),
                          }}
                          inputClassName="h-[34px] text-[13px]"
                        />
                        <p className="pt-1 text-[11px] text-text-dim">
                          {t("travelForm.ticketSplitOf", { amount: formatCurrency(fareValue) })}
                        </p>
                      </div>
                    ) : (
                      // No price yet: nothing to split, but who pays is still
                      // an answer ("patient pays, ticket not bought yet"). A
                      // fresh pick drops any old split, so the price typed
                      // later lands whole on the payer.
                      <div>
                        <PayerPicker
                          value={flightPayer}
                          onChange={(next) => {
                            if (next !== "clinic") setFareParty(next)
                            form.setValue("flight_payer", next, { shouldDirty: true })
                            form.setValue("flight_patient_amount", "", { shouldDirty: true })
                          }}
                          partner={leadAffiliator}
                          label={t("travelForm.ticketPaidBy")}
                          t={t}
                        />
                        <p className="pt-1 text-[11px] text-text-dim">
                          {t("travelForm.ticketSplitNoPrice")}
                        </p>
                      </div>
                    )}
                  </div>
                ) : travelPayersEnabled ? (
                  <PayerPicker
                    value={flightPayer}
                    onChange={(next) => form.setValue("flight_payer", next, { shouldDirty: true })}
                    partner={leadAffiliator}
                    label={t("travelForm.paidBy")}
                    t={t}
                  />
                ) : (
                  <PaysToggle
                    pressed={flightPayer === "patient"}
                    onToggle={() =>
                      form.setValue(
                        "flight_payer",
                        flightPayer === "patient" ? "clinic" : "patient",
                        { shouldDirty: true },
                      )
                    }
                    label={t("travelForm.clientPaysFlight")}
                    hint={
                      flightPayer === "patient"
                        ? t("travelForm.clientPaysFlightOn")
                        : t("travelForm.clientPaysFlightOff")
                    }
                  />
                )}
                {!fareSplitEnabled && flightPayer !== "clinic" && (
                  <div className="w-40">
                    <FieldLabel>{t("travelForm.flightCost")}</FieldLabel>
                    <div className="relative">
                      <Input
                        inputMode="decimal"
                        className="h-[34px] pr-8 font-mono text-[13px] placeholder:text-text-dim/60"
                        placeholder={t("travelForm.flightCostPlaceholder")}
                        {...form.register("flight_cost")}
                      />
                      <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-[12px] text-text-dim">
                        {getCurrencySymbol(clinicCurrency)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              )}

              {!isStay && !hotelNotesDeskOnly && <div className="hidden bg-border sm:block" />}

              {/* Accommodation */}
              {hotelNotesDeskOnly ? null : (
              <div className="grid content-start gap-2.5">
                <div className="flex min-h-[22px] items-center justify-between gap-2.5 pb-0.5">
                  <BlockTitle>{t("travel.accommodation")}</BlockTitle>
                  {/* Off the flag, the switch this block always had. */}
                  {!travelPayersEnabled && !isStay && (
                    <Switch
                      checked={accommodationOn}
                      onCheckedChange={(on) => setAccommodationMode(on ? "arranged" : "none")}
                      aria-label={t("travelForm.clinicArranges")}
                    />
                  )}
                </div>
                {/* ArtDent 260922 — three answers on their own full-width row,
                    in the Companions control's style: three words do not fit
                    beside the block title, and the header is the wrong place
                    for a choice this size. The declined state is amber — it is
                    a thing the desk must notice, not a fact filed away. */}
                {travelPayersEnabled && !isStay && (
                  <div
                    className="flex h-[34px] gap-0.5 rounded-[9px] border border-border bg-surface-2 p-0.5"
                    role="radiogroup"
                    aria-label={t("travelForm.clinicArranges")}
                  >
                    {(["arranged", "none", "declined"] as const).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        role="radio"
                        aria-checked={accommodationMode === mode}
                        onClick={() => setAccommodationMode(mode)}
                        className={cn(
                          "flex-1 whitespace-nowrap rounded-[7px] px-2 font-sans text-[12px] font-medium transition-colors",
                          accommodationMode === mode
                            ? mode === "declined"
                              ? "bg-amber-100 text-amber-800 shadow-sm"
                              : "bg-surface text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        {mode === "arranged"
                          ? t("travelForm.accommodationArranged")
                          : mode === "none"
                            ? t("travelForm.accommodationNone")
                            : t("travelForm.accommodationDeclined")}
                      </button>
                    ))}
                  </div>
                )}
                {accommodationMode === "declined" ? (
                  <p className="rounded-md bg-amber-50 px-3 py-2 text-[11.5px] text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
                    {t("travelForm.accommodationDeclinedHint")}
                  </p>
                ) : accommodationOn ? (
                  <>
                    {hotelLocked && (
                      <p className="rounded-md bg-emerald-50 px-3 py-2 text-[11.5px] text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                        {t("travelForm.hotelLockedHint")}
                      </p>
                    )}
                    <fieldset disabled={hotelLocked} className="grid min-w-0 gap-2.5">
                    <div>
                      <FieldLabel>{t("travelForm.hotel")}</FieldLabel>
                      {hotelsEnabled ? (
                        <HotelCombobox
                          hotelId={form.watch("hotel_id") ?? null}
                          hotelName={form.watch("hotel_name") ?? ""}
                          onChange={({ hotelId, hotelName }) => {
                            form.setValue("hotel_id", hotelId, { shouldDirty: true })
                            form.setValue("hotel_name", hotelName, { shouldDirty: true })
                          }}
                          placeholder={t("travelForm.hotelNamePlaceholder")}
                          addLabel={(name) => t("travelForm.hotelAdd", { name })}
                          emptyLabel={t("travelForm.hotelEmpty")}
                          pickOnlyEmptyLabel={t("travelForm.hotelPickOnlyEmpty")}
                          onAddFailed={() => toast.error(t("travelForm.toast.hotelAddFailed"))}
                        />
                      ) : (
                        <Input
                          className="h-[34px] text-[13px] placeholder:text-text-dim/60"
                          placeholder={t("travelForm.hotelNamePlaceholder")}
                          {...form.register("hotel_name")}
                        />
                      )}
                      {(stayConflictMessage ?? serverStayError) && (
                        <p role="alert" className="mt-1.5 text-[11.5px] text-destructive">
                          {stayConflictMessage ?? serverStayError}
                        </p>
                      )}
                    </div>
                    {hotelsEnabled && (
                      <div>
                        <div className="flex items-center justify-between gap-2">
                          <FieldLabel>{t("travelForm.hotelCost")}</FieldLabel>
                          {/* Same segmented control as the quote composer's
                              discount mode (quotes/sections/payment-step.tsx). */}
                          <span className="inline-flex items-center rounded-md border p-0.5">
                            {(["total", "per_night"] as const).map((m) => (
                              <button
                                key={m}
                                type="button"
                                onClick={() => form.setValue("hotel_cost_mode", m, { shouldDirty: true })}
                                className={cn(
                                  "min-w-6 rounded-sm px-1.5 py-0.5 text-[11px] font-medium transition-colors",
                                  costMode === m
                                    ? "bg-primary text-primary-foreground"
                                    : "text-muted-foreground hover:text-foreground"
                                )}
                              >
                                {m === "total"
                                  ? t("travelForm.hotelCostTotalShort")
                                  : t("travelForm.hotelCostPerNightShort")}
                              </button>
                            ))}
                          </span>
                        </div>
                        <div className="relative">
                          <Input
                            inputMode="decimal"
                            className="h-[34px] pr-8 text-[13px] placeholder:text-text-dim/60"
                            placeholder={
                              costMode === "total"
                                ? t("travelForm.hotelCostTotalPlaceholder")
                                : t("travelForm.hotelCostPerNightPlaceholder")
                            }
                            {...form.register("hotel_cost")}
                          />
                          <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-[12px] text-text-dim">
                            {getCurrencySymbol(clinicCurrency)}
                          </span>
                        </div>
                        {costHint && (
                          <p className="pt-1 text-[11px] text-text-dim">{costHint}</p>
                        )}
                        {rateApplied && rateSuggestion && (
                          <p className="pt-1 text-[11px] text-text-dim">
                            {t("travelForm.hotelRateApplied", {
                              amount: formatCurrency(rateSuggestion.rate.price_per_night),
                              persons: rateSuggestion.rate.persons,
                              hotel: rateSuggestion.hotel.name,
                            })}
                          </p>
                        )}
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2.5">
                      <div>
                        <FieldLabel>{t("travelForm.checkIn")}</FieldLabel>
                        <DateField
                          compact
                          value={form.watch("check_in_date")}
                          onChange={(value) => setStayDate("check_in_date", value)}
                          placeholder={t("travelForm.pickDate")}
                        />
                      </div>
                      <div>
                        <FieldLabel>{t("travelForm.checkOut")}</FieldLabel>
                        <DateField
                          compact
                          value={form.watch("check_out_date")}
                          onChange={(value) => setStayDate("check_out_date", value)}
                          placeholder={t("travelForm.pickDate")}
                        />
                      </div>
                    </div>
                    </fieldset>
                    {errors.check_out_date && (
                      <p className="text-[11.5px] text-destructive">
                        {errors.check_out_date.message}
                      </p>
                    )}
                    <p className="text-[11.5px] text-text-dim">
                      {isStay
                        ? t("travelForm.stayOwnBillHint")
                        : datesLinked
                          ? t("travelForm.stayLinkedHint")
                          : t("travelForm.stayUnlinkedHint")}
                    </p>
                    {/* The hotel's own rules, as the accommodation mandate
                        prints them — kept on the trip so the sheet and the
                        trip can not disagree (ArtDent 260922). */}
                    {travelPayersEnabled && (
                    <div className="grid grid-cols-2 gap-2.5">
                      {/* Soft time mask (ArtDent 260924): digits format like
                          the flight times ("1430" → "14:30"); wording such as
                          "after 14:00, on request" is kept as typed. */}
                      <div>
                        <FieldLabel>{t("travelForm.hotelCheckInTime")}</FieldLabel>
                        <TimeOrTextInput
                          maxLength={40}
                          className="h-[34px] text-[13px] placeholder:text-text-dim/60"
                          placeholder={t("travelForm.hotelCheckInTimePlaceholder")}
                          value={form.watch("hotel_check_in_time") ?? ""}
                          onChange={(value) =>
                            form.setValue("hotel_check_in_time", value, { shouldDirty: true })
                          }
                        />
                      </div>
                      <div>
                        <FieldLabel>{t("travelForm.hotelCheckOutTime")}</FieldLabel>
                        <TimeOrTextInput
                          maxLength={40}
                          className="h-[34px] text-[13px] placeholder:text-text-dim/60"
                          placeholder={t("travelForm.hotelCheckOutTimePlaceholder")}
                          value={form.watch("hotel_check_out_time") ?? ""}
                          onChange={(value) =>
                            form.setValue("hotel_check_out_time", value, { shouldDirty: true })
                          }
                        />
                      </div>
                    </div>
                    )}
                    {nightsSplitEnabled ? (
                      <NightsSplit
                        nights={stayNights}
                        patientNights={splitPatientNights}
                        party={hotelPayer === "affiliator" ? "affiliator" : hotelPayer === "patient" ? "patient" : splitParty}
                        partner={leadAffiliator}
                        onChange={({ patientNights, party }) => {
                          setSplitParty(party)
                          form.setValue("hotel_patient_nights", String(patientNights), { shouldDirty: true })
                          form.setValue("hotel_payer", patientNights > 0 ? party : "clinic", { shouldDirty: true })
                        }}
                        hint={
                          stayNights !== null
                            ? t("travelForm.nightsPaidOf", { count: stayNights })
                            : t("travelForm.payerNightsNoDates")
                        }
                        t={t}
                      />
                    ) : travelPayersEnabled ? (
                      <PayerPicker
                        value={hotelPayer}
                        onChange={(next) => form.setValue("hotel_payer", next, { shouldDirty: true })}
                        partner={leadAffiliator}
                        label={t("travelForm.paidBy")}
                        t={t}
                      />
                    ) : (
                      <PaysToggle
                        pressed={hotelPayer === "patient"}
                        onToggle={() =>
                          form.setValue(
                            "hotel_payer",
                            hotelPayer === "patient" ? "clinic" : "patient",
                            { shouldDirty: true },
                          )
                        }
                        label={t("travelForm.clientPaysHotel")}
                        hint={
                          hotelPayer === "patient"
                            ? t("travelForm.clientPaysHotelOn")
                            : t("travelForm.clientPaysHotelOff")
                        }
                      />
                    )}
                    {/* How many of the nights the other party carries — the
                        accommodation mandate's split, asked here too so the
                        trip means the same thing whichever form wrote it. */}
                    {travelPayersEnabled && !nightsSplitEnabled && hotelPayer !== "clinic" && (
                      <div className="flex items-end gap-2">
                        <div className="w-24">
                          <FieldLabel>{t("travelForm.payerNights")}</FieldLabel>
                          <Input
                            inputMode="numeric"
                            className="h-[34px] font-mono text-[13px] placeholder:text-text-dim/60"
                            placeholder={stayNights !== null ? String(stayNights) : ""}
                            {...form.register("hotel_patient_nights")}
                          />
                        </div>
                        <span className="pb-2 text-[11.5px] text-text-dim">
                          {stayNights !== null
                            ? t("travelForm.payerNightsOf", { count: stayNights })
                            : t("travelForm.payerNightsNoDates")}
                        </span>
                      </div>
                    )}
                    <div>
                      <FieldLabel>{t("travelForm.hotelNotes")}</FieldLabel>
                      <Textarea
                        rows={2}
                        className="min-h-12 resize-y text-[13px] leading-relaxed placeholder:text-text-dim/60"
                        placeholder={t("travelForm.hotelNotesPlaceholder")}
                        {...form.register("hotel_notes")}
                      />
                      {errors.hotel_notes && (
                        <p className="pt-1 text-[11.5px] text-destructive">
                          {errors.hotel_notes.message}
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-[11.5px] text-text-dim">
                    {t("travelForm.noAccommodationHint")}
                  </p>
                )}
              </div>
              )}
            </div>

            {/* Notes | Attachments */}
            {!isStay && (
            <div className={cn("mt-4 grid grid-cols-1 gap-y-5 border-t border-border pt-3.5", hotelNotesDeskOnly ? null : "sm:grid-cols-[1fr_1px_268px] sm:gap-x-[22px]")}>
              {/* Notes */}
              {hotelNotesDeskOnly ? null : (
              <div className="grid content-start gap-2">
                <div className="flex items-center justify-between gap-2.5">
                  <BlockTitle>{t("travelForm.notes")}</BlockTitle>
                  <span className="font-mono text-[11px] text-text-dim">
                    {t("travelForm.notesVisibility")}
                  </span>
                </div>
                <Textarea
                  rows={3}
                  className="min-h-16 resize-y text-[13px] leading-relaxed placeholder:text-text-dim/60"
                  placeholder={t("travelForm.notesPlaceholder")}
                  {...form.register("notes")}
                />
                <div className="flex flex-wrap gap-1.5">
                  {NOTE_CHIPS.map((chip) => (
                    <button
                      key={chip}
                      type="button"
                      onClick={() => {
                        const label = t(`travelForm.chips.${chip}`)
                        const current = form.getValues("notes") || ""
                        form.setValue(
                          "notes",
                          `${current ? current.replace(/\n$/, "") + "\n" : ""}${label}: `,
                        )
                      }}
                      className="h-6 rounded-full border border-border px-2.5 text-[11.5px] font-medium text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground"
                    >
                      + {t(`travelForm.chips.${chip}`)}
                    </button>
                  ))}
                </div>
              </div>

              )}
              {hotelNotesDeskOnly ? null : <div className="hidden bg-border sm:block" />}

              {/* Attachments */}
              <div className="grid content-start gap-2">
                <div className="flex items-center justify-between gap-2.5">
                  <BlockTitle>{t("travelForm.attachments")}</BlockTitle>
                  {fileCount > 0 && (
                    <span className="font-mono text-[11px] text-text-dim">
                      {fileCount}
                    </span>
                  )}
                </div>
                {fileCount > 0 && (
                  <div className="grid gap-1.5">
                    {attachments.map((media) => (
                      <FileRow
                        key={`m-${media.id}`}
                        name={media.name || media.file_name}
                        size={media.human_readable_size}
                        mime={media.mime_type}
                        onRemove={() => removeAttachment(media)}
                      />
                    ))}
                    {pendingFiles.map((file, index) => (
                      <FileRow
                        key={`p-${index}-${file.name}`}
                        name={file.name}
                        size={humanSize(file.size)}
                        mime={file.type}
                        onRemove={() =>
                          setPendingFiles((previous) =>
                            previous.filter((_, i) => i !== index),
                          )
                        }
                      />
                    ))}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_FILES}
                  className="hidden"
                  onChange={(event) => {
                    addFiles(Array.from(event.target.files ?? []))
                    event.target.value = ""
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault()
                    addFiles(Array.from(event.dataTransfer.files ?? []))
                  }}
                  className="flex h-[46px] w-full items-center justify-center gap-2 rounded-[10px] border border-dashed border-border-strong text-[12px] text-text-dim transition-colors hover:border-accent hover:bg-accent-tint-2 hover:text-muted-foreground"
                >
                  {isUploading ? (
                    <IconLoader2 className="size-3.5 animate-spin" />
                  ) : (
                    <IconUpload className="size-3.5" />
                  )}
                  <span>
                    {t("travelForm.dropFiles")}{" "}
                    <span className="border-b border-current text-accent-soft">
                      {t("travelForm.browse")}
                    </span>
                  </span>
                </button>
                <p className="text-[11.5px] text-text-dim">
                  {t("travelForm.filesHint")}
                </p>
              </div>
            </div>
            )}
          </div>

          {identityMissing && (
            <p
              role="alert"
              className="mx-6 mb-3 shrink-0 rounded-md border border-amber-300 bg-amber-50 p-3 text-[12.5px] text-amber-950 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
            >
              {t("travelForm.identityWarning")}
            </p>
          )}

          {/* Footer — route summary + actions */}
          <div className="flex shrink-0 items-center justify-between gap-4 border-t border-border bg-surface-2 px-6 py-3">
            <div className="min-w-0 truncate font-mono text-[12px] text-muted-foreground">
              {!isStay && summaryLegs.length > 0 && (
                <>
                  <span className="font-medium text-foreground">{summaryLegs[0]}</span>
                  {summaryLegs.length > 1 && (
                    <>
                      <span className="text-border-strong"> → </span>
                      <span className="font-medium text-foreground">{summaryLegs[1]}</span>
                    </>
                  )}
                  {nights !== null && nights > 0 && (
                    <>
                      <span className="text-border-strong"> · </span>
                      {t("travelForm.nights", { count: nights })}
                    </>
                  )}
                  <span className="text-border-strong"> · </span>
                </>
              )}
              {!isStay && t("travelForm.travellers", { count: 1 + (extraPassengers || 0) })}
            </div>
            <div className="flex shrink-0 gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {tc("cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting || stayConflict !== null || (travelCoordinationEnabled && checksLoading)}>
                {isSubmitting ? (
                  <IconLoader2 className="size-4 animate-spin" />
                ) : (
                  <IconCheck className="size-4" />
                )}
                {isStay
                  ? isEditing ? t("travelForm.updateStay") : t("travelForm.addStay")
                  : isEditing ? t("travelForm.updateTrip") : t("travelForm.addTrip")}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * "Client pays" toggle (Dentour 260911): a pressed chip beside the flights and
 * beside the stay. Off means the clinic covers it, so a trip nobody touched
 * claims nothing — the same posture as every trip recorded before the flags.
 */
/**
 * The yes/no "client pays" pill every clinic without travel_payers_enabled
 * still sees. Amber because it is a thing to collect, not a fact to file.
 */
function PaysToggle({
  pressed,
  onToggle,
  label,
  hint,
}: {
  pressed: boolean
  onToggle: () => void
  label: string
  hint: string
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-2.5 gap-y-1">
      <button
        type="button"
        aria-pressed={pressed}
        onClick={onToggle}
        className={cn(
          "inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11.5px] font-medium transition-colors",
          pressed
            ? "border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-100/80"
            : "border-border text-muted-foreground hover:bg-surface-2 hover:text-foreground",
        )}
      >
        <span
          className={cn("size-1.5 rounded-full", pressed ? "bg-amber-600" : "bg-border")}
          aria-hidden
        />
        {label}
      </button>
      <span className="text-[11px] text-text-dim">{hint}</span>
    </div>
  )
}

/**
 * ArtDent 260922 — who pays: the clinic, the patient, or the referral partner.
 * Same segmented control as the hotel cost's Total / per-night switch, so the
 * two read as one family. The partner segment exists only when the lead HAS a
 * partner — on any other lead the server would refuse it anyway.
 */
/**
 * Dentx 261016 (mandate_from_trip_enabled) — the stay's nights as two counts,
 * clinic and patient, always on screen. The mandate prints straight from the
 * trip, so this is the only place the split is asked. It writes the same two
 * fields as the payer picker + night count it replaces (hotel_payer,
 * hotel_patient_nights), so Hotel expenses and the sheet read it unchanged.
 * Editing either count makes the other the rest of the stay.
 */
function NightsSplit({
  nights,
  patientNights,
  party,
  partner,
  onChange,
  hint,
  t,
}: {
  /** The stay's nights; null until both dates are set. */
  nights: number | null
  patientNights: number
  /** Who carries the nights the clinic does not. */
  party: NightsParty
  partner: { name: string } | null
  onChange: (next: { patientNights: number; party: NightsParty }) => void
  hint: string
  t: (key: string) => string
}) {
  const patient = nights === null ? 0 : Math.min(Math.max(patientNights, 0), nights)
  // Typing over a count replaces it; a cleared box reads as 0.
  const toCount = (raw: string) => {
    const n = parseInt(raw.replace(/\D/g, ""), 10)

    return Number.isFinite(n) ? Math.min(Math.max(n, 0), nights ?? 0) : 0
  }
  const countInput = "h-[34px] font-mono text-[13px] placeholder:text-text-dim/60"

  return (
    <div>
      <FieldLabel>{t("travelForm.nightsPaidBy")}</FieldLabel>
      <div className="grid grid-cols-2 gap-2.5">
        <div className="grid gap-1">
          <span className="flex h-[22px] items-center text-[11.5px] text-text-dim">
            {t("travelForm.payerClinic")}
          </span>
          <Input
            inputMode="numeric"
            aria-label={t("travelForm.payerClinic")}
            className={countInput}
            disabled={nights === null}
            value={nights === null ? "" : String(nights - patient)}
            onChange={(e) => onChange({ patientNights: (nights ?? 0) - toCount(e.target.value), party })}
          />
        </div>
        <div className="grid gap-1">
          {partner ? (
            <span className="inline-flex h-[22px] w-fit items-center rounded-md border p-0.5">
              {(["patient", "affiliator"] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={party === option}
                  onClick={() => onChange({ patientNights: patient, party: option })}
                  title={option === "affiliator" ? partner.name : undefined}
                  className={cn(
                    "max-w-28 truncate rounded-sm px-1.5 text-[11px] font-medium transition-colors",
                    party === option
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {option === "patient"
                    ? t("travelForm.payerPatient")
                    : partner.name || t("travelForm.payerPartner")}
                </button>
              ))}
            </span>
          ) : (
            <span className="flex h-[22px] items-center text-[11.5px] text-text-dim">
              {t("travelForm.payerPatient")}
            </span>
          )}
          <Input
            inputMode="numeric"
            aria-label={
              party === "affiliator"
                ? partner?.name || t("travelForm.payerPartner")
                : t("travelForm.payerPatient")
            }
            className={countInput}
            disabled={nights === null}
            value={nights === null ? "" : String(patient)}
            onChange={(e) => onChange({ patientNights: toCount(e.target.value), party })}
          />
        </div>
      </div>
      <p className="pt-1 text-[11px] text-text-dim">{hint}</p>
    </div>
  )
}

type NightsParty = "patient" | "affiliator"

function PayerPicker({
  value,
  onChange,
  partner,
  label,
  t,
}: {
  value: TravelPayer
  onChange: (next: TravelPayer) => void
  partner: { name: string } | null
  label: string
  t: (key: string) => string
}) {
  const options: TravelPayer[] = partner
    ? ["clinic", "patient", "affiliator"]
    : ["clinic", "patient"]

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-2.5 gap-y-1">
      <span className="text-[11.5px] text-text-dim">{label}</span>
      <span className="inline-flex items-center rounded-md border p-0.5">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={value === option}
            onClick={() => onChange(option)}
            title={option === "affiliator" ? partner?.name : undefined}
            className={cn(
              "min-w-6 max-w-32 truncate rounded-sm px-1.5 py-0.5 text-[11px] font-medium transition-colors",
              value === option
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {option === "clinic"
              ? t("travelForm.payerClinic")
              : option === "patient"
                ? t("travelForm.payerPatient")
                : partner?.name || t("travelForm.payerPartner")}
          </button>
        ))}
      </span>
    </div>
  )
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1.5 block text-[11px] font-medium tracking-[0.02em] text-muted-foreground">
      {children}
    </span>
  )
}

function BlockTitle({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-mono text-[9.5px] font-medium uppercase tracking-[0.12em] text-text-dim">
      {children}
    </span>
  )
}

function ColHead({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={cn("pb-0.5 font-mono text-[9.5px] font-medium uppercase tracking-[0.1em] text-text-dim", className)}>
      {children}
    </span>
  )
}

/**
 * One flight clock in the Flights grid. `tight` trims the padding so "HH:MM"
 * still fits when the row carries two of them (Radiance 261019).
 */
function FlightTimeField({
  value,
  onChange,
  label,
  invalid,
  tight = false,
}: {
  value: string | undefined
  onChange: (value: string) => void
  label: string
  invalid: boolean
  tight?: boolean
}) {
  return (
    <TimeInput
      className={cn("h-[34px] font-mono text-[13px] placeholder:text-text-dim/60", tight && "px-1.5")}
      value={value ?? ""}
      onChange={onChange}
      aria-label={label}
      aria-invalid={invalid || undefined}
    />
  )
}

/**
 * shadcn date input (Popover + Calendar) over `yyyy-MM-dd` strings, styled to
 * the sheet's 34px field height with a mono date readout. Clicking the
 * selected day again deselects (the clear affordance).
 */
function DateField({
  value,
  onChange,
  placeholder,
  compact = false,
}: {
  value: string | undefined
  onChange: (value: string) => void
  placeholder: string
  compact?: boolean
}) {
  const clinicFormat = useFormatter()
  const selected = value ? new Date(value) : undefined

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          type="button"
          className={cn(
            "h-[34px] w-full justify-start gap-2 rounded-[9px] px-2.5 font-mono text-[13px] font-normal tracking-[-0.01em]",
            !value && "text-text-dim",
          )}
        >
          {!compact && <IconCalendar className="size-3.5 shrink-0 text-text-dim" />}
          <span className="truncate">
            {selected
              ? clinicFormat.dateTime(selected, { day: "numeric", month: "short", weekday: compact ? undefined : "short" })
              : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(day) =>
            onChange(day ? formatDateFns(day, "yyyy-MM-dd") : "")
          }
        />
      </PopoverContent>
    </Popover>
  )
}

function FileRow({
  name,
  size,
  mime,
  onRemove,
}: {
  name: string
  size: string
  mime: string
  onRemove: () => void
}) {
  const kind = mime === "application/pdf" ? "PDF" : fileExtLabel(name)
  const isPdf = kind === "PDF"

  return (
    <div className="grid h-[34px] grid-cols-[34px_1fr_auto_22px] items-center gap-2 rounded-[9px] border border-border bg-surface-2 pr-1.5">
      <span
        className={cn(
          "grid h-full place-items-center rounded-l-[8px] border-r border-border bg-surface-3 font-mono text-[8.5px] font-medium tracking-[0.06em]",
          isPdf ? "text-red-600 dark:text-red-400" : "text-accent-soft",
        )}
      >
        {kind}
      </span>
      <span className="truncate text-[12px]">{name}</span>
      <span className="font-mono text-[10.5px] text-text-dim">{size}</span>
      <button
        type="button"
        aria-label="Remove"
        onClick={onRemove}
        className="grid size-[22px] place-items-center rounded-md text-text-dim transition-colors hover:bg-surface-3 hover:text-foreground"
      >
        <IconX className="size-3" />
      </button>
    </div>
  )
}

function fileExtLabel(name: string): string {
  const ext = name.split(".").pop()?.toUpperCase() ?? "FILE"
  return ext.length > 4 ? "FILE" : ext
}

function humanSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}
